import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import * as _ from "lodash";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { editActions } from "../../actions/editActions";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import { Modal } from "semantic-ui-react";
import ButtonField from "../../components/Editing/ButtonField";

const { useState, useCallback } = React;

const faultCategoryFields = nameofFactory<Api.FaultCategory>();

type OwnProps = {
  toggleModal: () => void;
};

interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const EditMonitoringFaultCategory: React.FC<OwnProps> = ({ toggleModal }) => {
  const dispatch = useDispatch();
  const editedFaultCategory = useSelector(
    (state: State.Root) => state.editView.editedObject
  ) as Api.FaultCategory;
  const storedFaultCategory = useSelector(
    (state: State.Root) => state.editView.storedObject
  ) as Api.FaultCategory;
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const activeUser = useSelector((state: State.Root) => state.main.activeUser);
  const columns = useSelector((state: State.Root) => state.columns);
  const isShortenTextEnabled = useSelector((state: State.Root) => state.main.isShortenTextEnabled);

  const [isEditLocalizationStatusOpen, setIsEditLocalizationStatusOpen] = useState(false);
  const [locsOriginal, setLocsOriginal] = useState<Api.LocsFaultCategory[]>([]);
  const [locsEdited, setLocsEditedState] = useState<Api.LocsFaultCategory[]>([]);

  const isNew = !storedFaultCategory;
  const editorTitle = isNew ? "New Monitoring Fault Category" : "Monitoring Fault Category";

  const groupName: Api.GroupName = "Monitoring";
  const userPermissions = hasWriteAccess(activeUser, groupName);

  const toggleEditLocalizationStatusModal = useCallback(() => {
    setIsEditLocalizationStatusOpen(prevState => !prevState);
  }, []);

  const setLocalizationsEdited = useCallback(
    (newLocalizations: Api.LocsFaultCategory[], oldLocalizations: Api.LocsFaultCategory[]) => {
      setLocsEditedState(newLocalizations);
      setLocsOriginal(oldLocalizations);
      const englishLoc = newLocalizations.find(
        loc => loc.localeNavigation.locale === "en-US"
      );
      dispatch(
        editActions.setEdited({
          ...editedFaultCategory,
          locs: newLocalizations,
          ...(englishLoc?.text ? { name: englishLoc.text } : {})
        })
      );
    },
    [dispatch, editedFaultCategory]
  );

  const setLocalizationsNonEdited = useCallback((localizations: Api.LocsFaultCategory[]) => {
    setLocsOriginal(localizations);
  }, []);

  const englishTranslationsChanged = useCallback(
    () => editedFaultCategory.name !== storedFaultCategory?.name,
    [editedFaultCategory.name, storedFaultCategory]
  );

  const isFieldValid = (field: string): ErrorStatus => {
    const monitoringFaultCategories = pdApi.monitoringFaultCategories as Api.FaultCategory[] || [];

    switch (field) {
      case faultCategoryFields("enumValue"): {
        return monitoringFaultCategories.find(
          faultCategory =>
            faultCategory.id !== editedFaultCategory.id &&
            Number(faultCategory.enumValue) === Number(editedFaultCategory.enumValue)
        )
          ? { errorStatus: false, errorMsg: "Code must be unique." }
          : { errorStatus: true, errorMsg: "" };
      }

      case faultCategoryFields("enumName"): {
        const { enumName } = editedFaultCategory;
        if (!enumName || enumName.trim().length === 0) {
          return { errorStatus: false, errorMsg: "Enumeration is required." };
        }
        return monitoringFaultCategories.find(
          (faultCategory: Api.FaultCategory) =>
            faultCategory.id !== editedFaultCategory.id &&
            faultCategory.enumName === editedFaultCategory.enumName.trim()
        )
          ? { errorStatus: false, errorMsg: "Enumeration must be unique." }
          : { errorStatus: true, errorMsg: "" };
      }

      case faultCategoryFields("name"): {
        const { name } = editedFaultCategory;
        if (!name || name.trim().length === 0) {
          return { errorStatus: false, errorMsg: "Name is required." };
        }
        return { errorStatus: true, errorMsg: "" };
      }

      case faultCategoryFields("comment"):
        return { errorStatus: true, errorMsg: "" };

      default:
        return { errorStatus: true, errorMsg: "" };
    }
  };

  const isAllFieldsValid = (): boolean => {
    const temp = _.keys(editedFaultCategory);
    for (const key of temp) {
      if (!isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  };

  const errorEnumValue = isFieldValid(faultCategoryFields("enumValue"));
  const errorEnumName = isFieldValid(faultCategoryFields("enumName"));
  const errorName = isFieldValid(faultCategoryFields("name"));

  // Check if this fault category is linked to any monitoring faults
  const isLinkedToFaults = pdApi.monitoringFaults
    ? pdApi.monitoringFaults.some(
        (fault: Api.MonitoringFault) => fault.faultCategory?.id === editedFaultCategory.id
      )
    : false;

  return (
    <Editor
      itemName={editorTitle}
      originalObject={storedFaultCategory}
      editedObject={editedFaultCategory}
      hasWritePermission={userPermissions}
      isValid={isAllFieldsValid()}
      cancel={() => dispatch(editActions.cancelEditing())}
      update={() => {
        dispatch(
          editActions.setEdited({
            ...editedFaultCategory,
            locs: locsEdited
          })
        );
        dispatch(editActions.update("monitoringFaultCategories"));
      }}
      create={() => {
        dispatch(
          editActions.setEdited({
            ...editedFaultCategory,
            locs: locsEdited
          })
        );
        dispatch(editActions.add("monitoringFaultCategories"));
      }}
      remove={{
        callback: toggleModal,
        enabledTooltip: "monitoring fault category",
        disabledTooltip: "as it is linked to monitoring faults.",
        disabled: isLinkedToFaults
      }}
    >
      <NumberField
        id="code"
        label="Code"
        value={editedFaultCategory.enumValue}
        isError={!errorEnumValue.errorStatus}
        errorMessage={errorEnumValue.errorMsg}
        readonly={!isNew || !userPermissions}
        onChange={enumValue =>
          dispatch(
            editActions.setEdited({
              ...editedFaultCategory,
              enumValue
            })
          )
        }
      />
      <TextField
        id="enumeration"
        label="Enumeration"
        value={editedFaultCategory.enumName}
        readonly={!isNew || !userPermissions}
        isError={!errorEnumName.errorStatus}
        errorMessage={errorEnumName.errorMsg}
        onChange={enumName =>
          dispatch(
            editActions.setEdited({
              ...editedFaultCategory,
              enumName: enumName.trim()
            })
          )
        }
      />
      <TextField
        id="name"
        label="Name"
        value={editedFaultCategory.name}
        isError={!errorName.errorStatus}
        errorMessage={errorName.errorMsg}
        onChange={name => {
          dispatch(
            editActions.setEdited({
              ...editedFaultCategory,
              name: name.trim()
            })
          );
        }}
        readonly={!userPermissions}
      />
      <TextField
        id="comment"
        label="Comment"
        value={editedFaultCategory.comment}
        onChange={comment => {
          dispatch(
            editActions.setEdited({
              ...editedFaultCategory,
              comment: comment.trim()
            })
          );
        }}
        readonly={!userPermissions}
      />
      <ButtonField
        id="editLocalizationStatus"
        label="View translations"
        onButtonClick={toggleEditLocalizationStatusModal}
      />
      <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
        <EditLocalizationStatus
          itemType={"FaultCategory"}
          editedItem={{ ...editedFaultCategory, locs: locsEdited }}
          getOption="monitoringFaultCategories"
          columns={columns["Fault Category Localization Status"]}
          pdApi={pdApi}
          toggleEditLocalizationStatusModal={toggleEditLocalizationStatusModal}
          setLocalizationsEdited={setLocalizationsEdited}
          isShortenTextEnabled={isShortenTextEnabled}
          englishTranslationChanged={englishTranslationsChanged()}
          userPermissions={userPermissions}
          locsOriginal={locsOriginal || []}
          setLocalizationsNonEdited={setLocalizationsNonEdited}
        />
      </Modal>
      {!userPermissions && <p className="color-red">Your role doesn't have necessary rights</p>}
    </Editor>
  );
};

export default EditMonitoringFaultCategory;
